import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { DURATION, EASE_OUT } from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { CHILD_AGE_OPTIONS, MAX_PASSENGERS, MIN_ADULTS } from "../constants";
import { Fold, SwapText } from "./animated";
import { FieldError, FieldLabel, SelectInput } from "./fields";

/** Round gold "-" / "+" button that squeezes a little when pressed. */
const StepButton = ({ symbol, label, onClick, isDisabled }) => {
  const shouldReduceMotion = useReducedMotion();
  const canAnimatePress = !shouldReduceMotion && !isDisabled;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-label={label}
      whileHover={canAnimatePress ? { scale: 1.06 } : undefined}
      whileTap={canAnimatePress ? { scale: 0.88 } : undefined}
      transition={{ duration: 0.15, ease: EASE_OUT }}
      className={cn(
        "tw:size-8 tw:!rounded-full tw:!p-0 tw:bg-[#F9EDD3] tw:border tw:border-solid tw:border-[#DBBE78] tw:flex tw:items-center tw:justify-center tw:font-hanken-grotesk tw:!text-[14px] tw:!leading-none tw:text-[#282828] tw:transition-[opacity,background-color]",
        isDisabled ? "tw:opacity-40 tw:cursor-not-allowed" : "tw:hover:bg-[#F3E2BC]"
      )}
    >
      {symbol}
    </motion.button>
  );
};

/** One "Adult 11+ years old  (-) 2 (+)" box. */
const PassengerCounter = ({ title, ageRange, count, minCount, maxCount, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="tw:flex-1 tw:h-[80px] tw:px-4 tw:flex tw:items-center tw:justify-between tw:bg-white tw:border tw:border-solid tw:border-[#E8E8E8] tw:!rounded-[5px] tw:font-hanken-grotesk">
      <div className="tw:flex tw:flex-col">
        <span className="tw:text-[14px] tw:leading-[20px] tw:text-[#282828]">{title}</span>
        <span className="tw:text-[13px] tw:leading-[18.5px] tw:text-[#8D8D8D]">{ageRange}</span>
      </div>
      <div className="tw:flex tw:items-center tw:gap-x-3">
        <StepButton
          symbol="-"
          label={t("meetAssistInquiry.Passengers.Decrease", { name: title })}
          isDisabled={count <= minCount}
          onClick={() => onChange(count - 1)}
        />
        <span aria-live="polite" className="tw:min-w-[9px] tw:text-center tw:text-[14px] tw:leading-[20px] tw:text-[#282828] tw:tabular-nums">
          <SwapText value={count} />
        </span>
        <StepButton
          symbol="+"
          label={t("meetAssistInquiry.Passengers.Increase", { name: title })}
          isDisabled={count >= maxCount}
          onClick={() => onChange(count + 1)}
        />
      </div>
    </div>
  );
};

/**
 * Adult and child counters, plus one age dropdown per child.
 * The total is capped at MAX_PASSENGERS across both counters.
 */
export const PassengerCountField = ({ adults, children, childAges, errors, onChangeAdults, onChangeChildren, onChangeChildAge }) => {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const ageOptions = CHILD_AGE_OPTIONS.map((age) => ({
    value: String(age),
    label: t("meetAssistInquiry.Passengers.AgeOption", { age }),
  }));

  return (
    <div className="tw:flex tw:flex-col tw:gap-y-3">
      <FieldLabel label={t("meetAssistInquiry.Passengers.Label")} isRequired />
      <div className="tw:flex tw:flex-col tw:lg:flex-row tw:gap-[10px]">
        <PassengerCounter
          title={t("meetAssistInquiry.Passengers.Adult")}
          ageRange={t("meetAssistInquiry.Passengers.AdultAge")}
          count={adults}
          minCount={MIN_ADULTS}
          maxCount={MAX_PASSENGERS - children}
          onChange={onChangeAdults}
        />
        <PassengerCounter
          title={t("meetAssistInquiry.Passengers.Child")}
          ageRange={t("meetAssistInquiry.Passengers.ChildAge")}
          count={children}
          minCount={0}
          maxCount={MAX_PASSENGERS - adults}
          onChange={onChangeChildren}
        />
      </div>

      {/* Folds open with the first child and away when the last is removed. */}
      <Fold isOpen={children > 0}>
        <div className="tw:bg-[#F9F9F9] tw:!rounded-[5px] tw:p-4 tw:flex tw:flex-col tw:gap-y-[10px]">
          <span className="tw:font-hanken-grotesk tw:text-[12px] tw:leading-[17px] tw:text-[#8D8D8D]">
            {t("meetAssistInquiry.Passengers.ChildAgesHint")}
          </span>
          <div className="tw:grid tw:grid-cols-2 tw:sm:grid-cols-3 tw:gap-[10px]">
            <AnimatePresence initial={false}>
              {childAges.map((age, childIndex) => {
                const fieldId = `meet-assist-child-age-${childIndex}`;
                return (
                  // Children have no id of their own; their position is their identity.
                  <motion.div
                    key={childIndex}
                    initial={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.96 }}
                    transition={{ duration: DURATION.fast, ease: EASE_OUT }}
                  >
                    <SelectInput
                      id={fieldId}
                      value={age === "" ? "" : String(age)}
                      placeholder={t("meetAssistInquiry.Passengers.AgeNeeded")}
                      isPlaceholderRequired
                      options={ageOptions}
                      errorKey={errors[`childAge${childIndex}`]}
                      onChange={(pickedAge) => onChangeChildAge(childIndex, Number(pickedAge))}
                    />
                    <FieldError id={`${fieldId}-error`} errorKey={errors[`childAge${childIndex}`]} />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </Fold>
    </div>
  );
};
